import {
  Avatar,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router';
import { useAuth } from '../../../contexts/AuthContext';
import { useCurrentUserQuery, getUserDisplayName } from '../../../hooks/user/useUserQuery';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { MENU_ITEM_GLYPH_SIZE } from '../../ui/iconButtonMetrics';
import { getAvatarColorForUser } from '../../../utils/avatarColor';
import { ROUTES } from '../../../constants/routes';

/**
 * Account cluster at the bottom of the nav rail: who you are, account
 * settings, sign out.
 *
 * Everything about the workspace (switching, settings, members, invites, the
 * visualizer) lives in `WorkspaceMenu` at the top of the rail, so this menu is
 * about the person only.
 */
export function AccountMenu() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { data: currentUser } = useCurrentUserQuery(!!user);

  const displayName = getUserDisplayName(currentUser || null);
  const avatarColor = getAvatarColorForUser({
    userId: currentUser?.id,
    email: user?.email,
    name: displayName,
  });

  return (
    <Menu isLazy placement="right-end">
      <MenuButton aria-label="Account">
        <Avatar size="sm" name={displayName} bg={avatarColor.bg} color={avatarColor.color} />
      </MenuButton>
      <Portal>
        <MenuList zIndex="toast">
          <HStack px={3} py={2} spacing={3}>
            <Avatar size="sm" name={displayName} bg={avatarColor.bg} color={avatarColor.color} />
            <VStack align="start" spacing={0} minW={0}>
              <Text fontWeight="semibold" color="text.primary" noOfLines={1}>
                {displayName}
              </Text>
              {user?.email && (
                <Text fontSize="xs" color="text.secondary" noOfLines={1}>
                  {user.email}
                </Text>
              )}
            </VStack>
          </HStack>
          <MenuDivider />
          <MenuItem
            icon={<MaterialIcon name="person" color="inherit" boxSize={MENU_ITEM_GLYPH_SIZE} />}
            onClick={() => navigate(`${ROUTES.settings}?tab=profile`)}
          >
            Account settings
          </MenuItem>
          <MenuDivider />
          <MenuItem
            icon={<MaterialIcon name="logout" color="inherit" boxSize={MENU_ITEM_GLYPH_SIZE} />}
            onClick={signOut}
            color="text.error"
          >
            Sign out
          </MenuItem>
        </MenuList>
      </Portal>
    </Menu>
  );
}
